// Archery Clash: Target Mania Server Engine (2-8 Players)
export class ArcheryEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.players = [];
    this.currentRound = 1;
    this.totalRounds = 3;
    this.currentShooterIdx = 0;
    this.scores = {};
    this.wind = 0; // -5 to +5
    this.targetY = 240;
    this.targetDir = 1;
    this.loopInterval = null;
  }

  start() {
    const targetCount = Math.min(8, Math.max(2, this.room.settings?.targetPlayers || 4));
    const shouldFillBots = this.room.settings?.fillBots !== false;

    this.players = this.room.players.slice(0, targetCount).map(p => ({ id: p.id, name: p.name, isBot: !!p.isBot }));

    if (shouldFillBots && this.players.length < targetCount) {
      const bots = [
        { id: 'bot_robin', name: 'Robin Bot 🏹', isBot: true },
        { id: 'bot_hawk', name: 'Hawkeye Bot 🎯', isBot: true }
      ];
      for (let i = this.players.length; i < targetCount; i++) {
        this.players.push(bots[(i - this.players.length) % bots.length]);
      }
    }

    this.scores = {};
    this.players.forEach(p => { this.scores[p.id] = 0; });
    this.currentRound = 1;
    this.currentShooterIdx = 0;
    this.generateWind();

    this.io.to(this.room.code).emit('archery_started', {
      players: this.players,
      currentShooterId: this.players[this.currentShooterIdx]?.id,
      round: this.currentRound,
      totalRounds: this.totalRounds,
      wind: this.wind
    });

    if (this.loopInterval) clearInterval(this.loopInterval);
    this.loopInterval = setInterval(() => this.updateTarget(), 1000 / 30);

    this.checkBotTurn();
  }

  generateWind() {
    this.wind = parseFloat(((Math.random() - 0.5) * 6).toFixed(1));
  }

  updateTarget() {
    this.targetY += this.targetDir * 2.5;
    if (this.targetY > 360) this.targetDir = -1;
    if (this.targetY < 120) this.targetDir = 1;

    this.io.to(this.room.code).emit('archery_targetState', {
      targetY: this.targetY,
      wind: this.wind
    });
  }

  checkBotTurn() {
    const cp = this.players[this.currentShooterIdx];
    if (cp && cp.isBot) {
      setTimeout(() => {
        const angle = -0.05 + (Math.random() - 0.5) * 0.15;
        const power = 16 + Math.random() * 4;
        this.shootArrow(cp.id, angle, power);
      }, 1500);
    }
  }

  shootArrow(playerId, angle, power) {
    const cp = this.players[this.currentShooterIdx];
    if (!cp || cp.id !== playerId) return;

    // Simulate Arrow Flight & Hit Target at X=700
    // Starting from X=100, Y=260
    const startX = 100, startY = 260;
    const vx = Math.cos(angle) * power;
    const vy = Math.sin(angle) * power;

    // Time to reach target X=700
    const t = (700 - startX) / vx;
    // Y position at hit (with gravity 0.35 and wind influence)
    const hitY = startY + vy * t + 0.5 * 0.35 * t * t + this.wind * 4;

    // Calculate score based on distance to target center
    const dist = Math.abs(hitY - this.targetY);
    let points = 0;
    if (dist < 10) points = 10; // Bullseye
    else if (dist < 22) points = 8;
    else if (dist < 36) points = 6;
    else if (dist < 50) points = 4;
    else if (dist < 65) points = 2;

    this.scores[playerId] = (this.scores[playerId] || 0) + points;

    this.io.to(this.room.code).emit('archery_arrowShot', {
      shooterId: playerId,
      angle,
      power,
      hitY,
      targetY: this.targetY,
      points,
      scores: this.scores
    });

    setTimeout(() => this.nextTurn(), 2800);
  }

  nextTurn() {
    this.currentShooterIdx++;
    if (this.currentShooterIdx >= this.players.length) {
      this.currentShooterIdx = 0;
      this.currentRound++;

      if (this.currentRound > this.totalRounds) {
        this.endGame();
        return;
      }
    }

    this.generateWind();
    this.io.to(this.room.code).emit('archery_turnChange', {
      currentShooterId: this.players[this.currentShooterIdx]?.id,
      round: this.currentRound,
      wind: this.wind
    });

    this.checkBotTurn();
  }

  endGame() {
    if (this.loopInterval) clearInterval(this.loopInterval);
    const sorted = [...this.players].sort((a, b) => (this.scores[b.id] || 0) - (this.scores[a.id] || 0));
    this.io.to(this.room.code).emit('archery_gameOver', {
      winner: sorted[0],
      leaderboard: sorted.map(p => ({ id: p.id, name: p.name, score: this.scores[p.id] || 0 }))
    });
  }

  destroy() {
    if (this.loopInterval) clearInterval(this.loopInterval);
  }
}
